// ---------------------------------------------------------------------------
// HRIS · Staff Attendance — QR generator (admin-facing)
// ---------------------------------------------------------------------------
// Generates a scannable QR that encodes a public check-in URL for the school.
// Staff scan it from their phones and are taken to the check-in page to submit
// attendance. School name / code / id are read from localStorage userInfo.
// ---------------------------------------------------------------------------
import React, {useMemo, useRef, useState} from "react";
import {
    Alert,
    AlertIcon,
    Badge,
    Box,
    Button,
    Flex,
    Grid,
    Heading,
    HStack,
    Input,
    InputGroup,
    InputRightElement,
    Text,
    useClipboard,
    useColorModeValue,
    useToast,
} from "@chakra-ui/react";
import {QRCodeCanvas} from "qrcode.react";
import {RepeatIcon} from "@chakra-ui/icons";
import {FiCopy, FiDownload, FiPrinter} from "react-icons/fi";
import {MdQrCode2} from "react-icons/md";

import Card from "../../../../../components/Card/Card.js";
import CardBody from "../../../../../components/Card/CardBody.js";
import CardHeader from "../../../../../components/Card/CardHeader.js";
import {
    buildCheckinUrl,
    getSchoolContext,
    makeSessionToken,
    todayISO,
} from "../../utils/attendanceQr";

const prettyDate = (iso) => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-US", {weekday: "long", year: "numeric", month: "long", day: "numeric"});
};

function StaffAttendanceQr() {
    const toast = useToast();
    const textColor = useColorModeValue("gray.700", "white");
    const mutedColor = useColorModeValue("gray.500", "gray.400");
    const qrRef = useRef(null);

    const school = useMemo(() => getSchoolContext(), []);
    const missingSchool = !school.schoolName && !school.schoolCode;

    const [date] = useState(todayISO());
    const [session, setSession] = useState(() => makeSessionToken());

    const checkinUrl = useMemo(
        () =>
            buildCheckinUrl({
                schoolCode: school.schoolCode,
                schoolName: school.schoolName,
                schoolId: school.schoolId,
                session,
                date,
            }),
        [school, session, date]
    );

    const {onCopy, hasCopied} = useClipboard(checkinUrl);

    const regenerate = () => {
        setSession(makeSessionToken());
        toast({
            title: "New QR generated",
            description: "The previous QR is now replaced with a fresh session.",
            status: "info",
            duration: 3000,
            isClosable: true,
        });
    };

    const downloadPng = () => {
        const canvas = qrRef.current?.querySelector("canvas");
        if (!canvas) return;
        const url = canvas.toDataURL("image/png");
        const a = document.createElement("a");
        a.href = url;
        a.download = `staff-attendance-qr-${school.schoolCode || "school"}-${date}.png`;
        a.click();
    };

    const printQr = () => {
        const canvas = qrRef.current?.querySelector("canvas");
        const dataUrl = canvas ? canvas.toDataURL("image/png") : "";
        const win = window.open("", "_blank", "width=480,height=640");
        if (!win) return;
        win.document.write(`
            <html>
              <head><title>Staff Attendance QR</title></head>
              <body style="font-family:sans-serif;text-align:center;padding:32px;">
                <h2 style="margin:0 0 4px">${school.schoolName || "School"}</h2>
                ${school.schoolCode ? `<div style="color:#666;margin-bottom:4px">Code: ${school.schoolCode}</div>` : ""}
                <div style="color:#666;margin-bottom:16px">${prettyDate(date)}</div>
                <img src="${dataUrl}" style="width:280px;height:280px" />
                <p style="color:#444;margin-top:16px">Scan to mark your attendance</p>
              </body>
            </html>
        `);
        win.document.close();
        win.focus();
        win.print();
    };

    return (
        <Flex direction="column" pt={{base: "120px", md: "75px"}} gap="20px">
            <Box>
                <Heading as="h1" size="lg" color={textColor}>Staff Attendance QR</Heading>
                <Text color={mutedColor} mt="4px">
                    Display this QR at the entrance. Staff scan it to mark today's attendance.
                </Text>
            </Box>

            {missingSchool && (
                <Alert status="warning" borderRadius="12px">
                    <AlertIcon/>
                    <Text flex="1">
                        Your school name / code could not be found in your login profile, so the QR may not
                        identify your school correctly. Please re-login or contact support.
                    </Text>
                </Alert>
            )}

            <Grid templateColumns={{base: "1fr", lg: "1fr 1fr"}} gap="24px">
                {/* QR card */}
                <Card>
                    <CardHeader mb="12px">
                        <Flex align="center" gap="8px">
                            <MdQrCode2 size="22px"/>
                            <Text fontSize="lg" fontWeight="bold" color={textColor}>Today's QR</Text>
                        </Flex>
                    </CardHeader>
                    <CardBody>
                        <Flex direction="column" align="center" w="100%" gap="16px">
                            <Box
                                ref={qrRef}
                                bg="white"
                                p="16px"
                                borderRadius="16px"
                                boxShadow="sm"
                                border="1px solid"
                                borderColor="gray.100"
                            >
                                <QRCodeCanvas
                                    value={checkinUrl}
                                    size={240}
                                    level="M"
                                    marginSize={4}
                                />
                            </Box>
                            <HStack spacing="10px" wrap="wrap" justify="center">
                                <Button leftIcon={<FiDownload/>} size="sm" variant="outline" onClick={downloadPng}>
                                    Download
                                </Button>
                                <Button leftIcon={<FiPrinter/>} size="sm" variant="outline" onClick={printQr}>
                                    Print
                                </Button>
                                <Button leftIcon={<RepeatIcon/>} size="sm" colorScheme="teal" onClick={regenerate}>
                                    Regenerate
                                </Button>
                            </HStack>
                        </Flex>
                    </CardBody>
                </Card>

                {/* Details card */}
                <Card>
                    <CardHeader mb="12px">
                        <Text fontSize="lg" fontWeight="bold" color={textColor}>Session details</Text>
                    </CardHeader>
                    <CardBody>
                        <Flex direction="column" gap="16px" w="100%">
                            <Box>
                                <Text fontSize="sm" color={mutedColor} fontWeight="bold">School</Text>
                                <Text fontSize="md" color={textColor}>{school.schoolName || "—"}</Text>
                            </Box>
                            <HStack spacing="24px">
                                <Box>
                                    <Text fontSize="sm" color={mutedColor} fontWeight="bold">School Code</Text>
                                    <Text fontSize="md" color={textColor}>{school.schoolCode || "—"}</Text>
                                </Box>
                                <Box>
                                    <Text fontSize="sm" color={mutedColor} fontWeight="bold">Date</Text>
                                    <Text fontSize="md" color={textColor}>{prettyDate(date)}</Text>
                                </Box>
                            </HStack>
                            <Box>
                                <Text fontSize="sm" color={mutedColor} fontWeight="bold">Session</Text>
                                <Badge colorScheme="purple" borderRadius="6px">{session}</Badge>
                            </Box>
                            <Box>
                                <Text fontSize="sm" color={mutedColor} fontWeight="bold" mb="6px">Check-in link</Text>
                                <InputGroup size="sm">
                                    <Input value={checkinUrl} isReadOnly pr="4.5rem" fontSize="xs"/>
                                    <InputRightElement width="4.5rem">
                                        <Button h="1.6rem" size="xs" leftIcon={<FiCopy/>} onClick={onCopy}>
                                            {hasCopied ? "Copied" : "Copy"}
                                        </Button>
                                    </InputRightElement>
                                </InputGroup>
                                <Text fontSize="xs" color={mutedColor} mt="8px">
                                    Staff who cannot scan can open this link directly on their phone.
                                </Text>
                            </Box>
                        </Flex>
                    </CardBody>
                </Card>
            </Grid>
        </Flex>
    );
}

export default StaffAttendanceQr;
